/**
 * QC charts — Bowling tab.
 *   Bowler spells · Line & length by bowler · Delivery variations ·
 *   Release point · Swing & seam movement
 */
import { useMemo, useState } from "react";
import {
  BAND_BY_KEY, MOVEMENT_TYPES, OUTCOME_LEGEND, RELEASE_POINTS, RELEASE_POSITIONS, VARIATION_TYPES,
  bowlerWicket, connGroup, conceded, econ, inOrder, oversText, pct0,
} from "./qcData";
import { Bar, Card, Empty, Legend, MiniPitch, Toggle } from "./qcUi";
import { useTip } from "./useTip";

// One row per bowler in bowling order, with the balls they bowled.
function bowlerRows(balls) {
  const m = new Map();
  for (const b of inOrder(balls)) {
    if (!b.bowlerId) continue;
    const key = `${b.innings}|${b.bowlerId}`;
    const r = m.get(key) || {
      key, id: b.bowlerId, name: b.bowler, innings: b.innings, team: b.bowlingTeamName, lefty: b.bowlerLefty,
      balls: [], legal: 0, runs: 0, wkts: 0, dots: 0, fours: 0, sixes: 0, wides: 0, noballs: 0, overs: new Map(),
    };
    r.balls.push(b);
    const c = conceded(b);
    r.runs += c;
    if (b.legal) r.legal += 1;
    if (bowlerWicket(b)) r.wkts += 1;
    if (b.legal && c === 0) r.dots += 1;
    if (b.offBat === 4) r.fours += 1;
    if (b.offBat === 6) r.sixes += 1;
    if (b.isWide) r.wides += 1;
    if (b.isNoBall) r.noballs += 1;
    const o = r.overs.get(b.over) || { legal: 0, runs: 0 };
    o.legal += b.legal ? 1 : 0;
    o.runs += c;
    r.overs.set(b.over, o);
    m.set(key, r);
  }
  return [...m.values()].map((r) => ({
    ...r,
    maidens: [...r.overs.values()].filter((o) => o.legal === 6 && o.runs === 0).length,
  }));
}

// ── 11. Bowler spells ────────────────────────────────────────────────
function BowlerSpells({ balls }) {
  const rows = useMemo(() => bowlerRows(balls), [balls]);
  if (!rows.length) return <Card wide title="Bowling figures"><Empty>No balls in this selection.</Empty></Card>;
  const inns = [...new Set(rows.map((r) => r.innings))];
  return (
    <Card wide title="Bowling figures" subtitle="Overs · maidens · runs · wickets per bowler, with dots and boundaries conceded">
      <div className="space-y-4">
        {inns.map((i) => {
          const rs = rows.filter((r) => r.innings === i);
          const maxEcon = Math.max(1, ...rs.map((r) => (r.legal ? (r.runs * 6) / r.legal : 0)));
          return (
            <div key={i} className="overflow-x-auto">
              <div className="text-[11px] text-text-secondary mb-1">Innings {i} · {rs[0].team} bowling</div>
              <table className="w-full text-[11px] min-w-[620px]">
                <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
                  <tr>
                    <th className="text-left py-1 font-normal">Bowler</th><th className="text-right font-normal">O</th><th className="text-right font-normal">M</th>
                    <th className="text-right font-normal">R</th><th className="text-right font-normal">W</th><th className="text-left font-normal pl-3 w-[20%]">Econ</th>
                    <th className="text-right font-normal">Dot %</th><th className="text-right font-normal">4s</th><th className="text-right font-normal">6s</th>
                    <th className="text-right font-normal">Wd</th><th className="text-right font-normal">Nb</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {rs.map((r) => (
                    <tr key={r.key} className="border-t border-border-default">
                      <td className="py-1.5 text-text-primary whitespace-nowrap">{r.name} <span className="text-[9px] text-text-secondary">{r.lefty ? "LA" : "RA"}</span></td>
                      <td className="text-right text-text-primary">{oversText(r.legal)}</td>
                      <td className="text-right text-text-secondary">{r.maidens}</td>
                      <td className="text-right text-text-primary">{r.runs}</td>
                      <td className="text-right text-text-primary font-semibold">{r.wkts}</td>
                      <td className="pl-3"><Bar value={r.legal ? (r.runs * 6) / r.legal : 0} max={maxEcon} color="var(--qc-s2)" label={econ(r.runs, r.legal)} /></td>
                      <td className="text-right text-text-secondary">{pct0(r.dots, r.legal)}</td>
                      <td className="text-right text-text-secondary">{r.fours}</td>
                      <td className="text-right text-text-secondary">{r.sixes}</td>
                      <td className="text-right text-text-secondary">{r.wides || ""}</td>
                      <td className="text-right text-text-secondary">{r.noballs || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── 12. Line & length by bowler ──────────────────────────────────────
const GOOD = new Set(["Length ball", "Back of a length"]);
const CORRIDOR = new Set(["OO", "OS"]);

function BowlerPitchMaps({ balls }) {
  const rows = useMemo(() => bowlerRows(balls).filter((r) => r.balls.some((b) => BAND_BY_KEY[b.length])), [balls]);
  if (!rows.length) return <Card wide title="Line & length by bowler"><Empty>No lengths coded in this selection.</Empty></Card>;
  return (
    <Card wide title="Line & length by bowler" subtitle="Where each bowler pitched it · good length = length ball or back of a length · corridor = outside off or off stump">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-3">
        {rows.map((r) => {
          const pitched = r.balls.filter((b) => BAND_BY_KEY[b.length]);
          const good = pitched.filter((b) => GOOD.has(b.length)).length;
          const corridor = pitched.filter((b) => CORRIDOR.has(b.line)).length;
          return (
            <div key={r.key} className="rounded-lg border border-border-default p-2.5">
              <div className="flex items-baseline justify-between gap-1 min-w-0">
                <span className="text-[12px] font-semibold text-text-primary truncate">{r.name}</span>
                <span className="text-[10px] text-text-secondary tabular-nums shrink-0">{r.wkts}/{r.runs}</span>
              </div>
              <div className="text-[10px] text-text-secondary tabular-nums mb-1.5">
                good length <span className="text-text-primary">{pct0(good, pitched.length)}</span> · corridor <span className="text-text-primary">{pct0(corridor, pitched.length)}</span>
              </div>
              <div className="max-w-[150px] mx-auto"><MiniPitch balls={pitched} /></div>
              <div className="text-[9px] text-text-secondary text-center mt-1">{pitched.length} balls · off ← → leg (RHB)</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex justify-center"><Legend items={OUTCOME_LEGEND} /></div>
    </Card>
  );
}

// Shared by the variation and movement charts: outcome per delivery type.
function typeTable(balls, types) {
  const m = new Map();
  for (const b of balls) {
    if (!b.delivery) continue;
    const known = types.find((t) => t.toLowerCase() === b.delivery.toLowerCase());
    if (!known) continue;
    const r = m.get(known) || { type: known, n: 0, legal: 0, runs: 0, dots: 0, wkts: 0, coded: 0, false: 0, bound: 0 };
    r.n += 1;
    if (b.legal) r.legal += 1;
    const c = conceded(b);
    r.runs += c;
    if (b.legal && c === 0) r.dots += 1;
    if (bowlerWicket(b)) r.wkts += 1;
    if (b.offBat === 4 || b.offBat === 6) r.bound += 1;
    const g = connGroup(b);
    if (g && b.faced) { r.coded += 1; if (g === "false") r.false += 1; }
    m.set(known, r);
  }
  return [...m.values()].sort((a, b) => b.n - a.n);
}

function TypeTable({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  const total = rows.reduce((t, r) => t + r.n, 0);
  return (
    <table className="w-full text-[11px]">
      <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
        <tr>
          <th className="text-left py-1 font-normal">Delivery</th><th className="font-normal w-[26%]" />
          <th className="text-right font-normal">Runs</th><th className="text-right font-normal">Econ</th>
          <th className="text-right font-normal">Dot %</th><th className="text-right font-normal">4s+6s</th>
          <th className="text-right font-normal">False</th><th className="text-right font-normal">W</th>
        </tr>
      </thead>
      <tbody className="tabular-nums">
        {rows.map((r) => (
          <tr key={r.type} className="border-t border-border-default">
            <td className="py-1.5 pr-2 text-text-primary whitespace-nowrap">{r.type}</td>
            <td className="pr-2"><Bar value={r.n} max={max} label={`${r.n} · ${pct0(r.n, total)}`} /></td>
            <td className="text-right text-text-primary">{r.runs}</td>
            <td className="text-right text-text-secondary">{econ(r.runs, r.legal)}</td>
            <td className="text-right text-text-secondary">{pct0(r.dots, r.legal)}</td>
            <td className="text-right text-text-secondary">{r.bound}</td>
            <td className="text-right text-text-secondary">{r.coded ? pct0(r.false, r.coded) : "—"}</td>
            <td className="text-right text-text-primary font-semibold">{r.wkts || ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BowlerPicker({ balls, value, onChange }) {
  const bowlers = useMemo(() => {
    const m = new Map();
    for (const b of balls) if (b.bowlerId && b.delivery) m.set(b.bowlerId, b.bowler);
    return [...m.entries()];
  }, [balls]);
  if (bowlers.length < 2) return null;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="bg-bg-primary border border-border-default rounded-md px-2 py-0.5 text-[11px] text-text-primary">
      <option value="">All bowlers</option>
      {bowlers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
    </select>
  );
}

// ── 13. Delivery variations ──────────────────────────────────────────
function DeliveryVariations({ balls }) {
  const [bowler, setBowler] = useState("");
  const rows = useMemo(() => typeTable(bowler ? balls.filter((b) => b.bowlerId === bowler) : balls, VARIATION_TYPES), [balls, bowler]);
  return (
    <Card title="Delivery variations" subtitle="Changes of pace and every spin delivery, with what each conceded · False = batter's false shots"
      controls={<BowlerPicker balls={balls} value={bowler} onChange={setBowler} />}>
      {rows.length ? <TypeTable rows={rows} /> : <Empty>No variations coded in this selection.</Empty>}
    </Card>
  );
}

// ── 15. Swing & seam movement ────────────────────────────────────────
function SwingSeam({ balls }) {
  const [bowler, setBowler] = useState("");
  const rows = useMemo(() => typeTable(bowler ? balls.filter((b) => b.bowlerId === bowler) : balls, MOVEMENT_TYPES), [balls, bowler]);
  return (
    <Card title="Swing & seam" subtitle="How the pace bowlers moved it, from the coded delivery type · False = batter's false shots"
      controls={<BowlerPicker balls={balls} value={bowler} onChange={setBowler} />}>
      {rows.length ? <TypeTable rows={rows} /> : <Empty>No swing or seam movement coded in this selection.</Empty>}
    </Card>
  );
}

// ── 14. Release point ────────────────────────────────────────────────
const POS_COLOR = {
  OVER_THE_WICKET: "var(--qc-s1)", ROUND_THE_WICKET: "var(--qc-s2)",
  WIDE_OVER: "rgba(148,163,184,0.85)", WIDE_ROUND: "rgba(148,163,184,0.55)", DIAGONAL: "rgba(148,163,184,0.3)",
};

function ReleasePoint({ balls }) {
  const tip = useTip();
  const [mode, setMode] = useState("position");
  const cats = mode === "position" ? RELEASE_POSITIONS : RELEASE_POINTS;
  const field = mode === "position" ? "releasePos" : "releasePoint";
  const rows = useMemo(() => {
    const m = new Map();
    for (const b of balls) {
      if (!b.bowlerId || !b[field]) continue;
      const r = m.get(b.bowlerId) || { id: b.bowlerId, name: b.bowler, n: 0, c: {}, out: {} };
      r.n += 1;
      r.c[b[field]] = (r.c[b[field]] || 0) + 1;
      const o = r.out[b[field]] || (r.out[b[field]] = { runs: 0, legal: 0, wkts: 0 });
      o.runs += conceded(b);
      if (b.legal) o.legal += 1;
      if (bowlerWicket(b)) o.wkts += 1;
      m.set(b.bowlerId, r);
    }
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [balls, field]);
  const pointColor = (k) => (k === "NORMAL" ? "rgba(148,163,184,0.45)" : k === "WIDE_OF_CREASE" ? "var(--qc-s1)" : k === "BEHIND_CREASE" ? "var(--qc-s2)" : "#facc15");
  const color = (k) => (mode === "position" ? POS_COLOR[k] || "rgba(148,163,184,0.5)" : pointColor(k));
  return (
    <Card title="Release point"
      subtitle={mode === "position" ? "Where each bowler delivered from — over / round the wicket, wide of the crease" : "Release anomalies — wide of or behind the crease, low arm"}
      controls={<Toggle label="Release view" value={mode} onChange={setMode} options={[{ key: "position", label: "Position" }, { key: "point", label: "Anomaly" }]} />}>
      {!rows.length ? <Empty>No release data coded in this selection.</Empty> : (
        <div data-tip-root className="relative">
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-[110px_1fr_40px] items-center gap-2 text-[11px]">
                <span className="text-text-primary truncate">{r.name}</span>
                <div className="flex h-3 rounded-[3px] overflow-hidden" style={{ gap: 1 }}>
                  {cats.filter((c) => r.c[c.key]).map((c) => (
                    <div key={c.key} style={{ flexGrow: r.c[c.key], background: color(c.key) }}
                      onMouseMove={(ev) => {
                        const o = r.out[c.key];
                        tip.show(ev, [`${r.name} · ${c.label}`, `${r.c[c.key]} balls (${pct0(r.c[c.key], r.n)})`, `${o.runs} runs · econ ${econ(o.runs, o.legal)} · ${o.wkts} wkts`]);
                      }}
                      onMouseLeave={tip.hide} />
                  ))}
                </div>
                <span className="text-right text-text-secondary tabular-nums">{r.n}</span>
              </div>
            ))}
          </div>
          <div className="mt-3"><Legend items={cats.map((c) => ({ label: c.label, color: color(c.key) }))} /></div>
          {tip.node}
        </div>
      )}
    </Card>
  );
}

export default function BowlingCharts({ balls }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <BowlerSpells balls={balls} />
      <BowlerPitchMaps balls={balls} />
      <DeliveryVariations balls={balls} />
      <SwingSeam balls={balls} />
      <ReleasePoint balls={balls} />
    </div>
  );
}
